import { useEffect, useMemo, useCallback, useContext } from "react";
import { Box, FormControl, TextField, useMediaQuery } from "@mui/material";
import { TopBarContext } from "../../providers/TopBarProvider";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { validateField, toCapitalize } from "../../common/globalFunctions";
import { LoadingContext } from "../../providers/LoadingProvider";
import { ToastContext } from "../../providers/ToastProvider";
import ClientBranchService from "../../services/ClientBranchService";
import { useNavigate, useLocation } from "react-router-dom";

const NewClientBranch = ({ edit }) => {
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { setOpenLoading, setTextLoading } = useContext(LoadingContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const bpMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const clientBranchService = useMemo(() => new ClientBranchService(), []);
  
  // Obtener clientId de la URL o del state
  const clientId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const idIndex = pathParts.indexOf("clients") + 1;
    return Number(pathParts[idIndex]) || location.state?.clientId;
  }, [location]);

  const onSubmit = useCallback(
    async (data) => {
      setTextLoading(edit ? "Actualizando sucursal" : "Agregando sucursal");
      setOpenLoading(true);

      data.branchName = toCapitalize(data.branchName);
      data.personInChargeName = toCapitalize(data.personInChargeName);

      try {
        if (edit) {
          await clientBranchService.update(data);
        } else {
          data.clientId = clientId;
          await clientBranchService.create(data);
        }

        setToastMessage([
          {
            severity: "success",
            primary: `Sucursal ${edit ? "editada" : "agregada"} exitosamente`,
          },
        ]);
      } catch {
        setToastMessage([
          {
            severity: "error",
            primary: `Error al ${
              edit ? "editar" : "agregar"
            } sucursal, por favor intentelo nuevamente`,
          },
        ]);
      } finally {
        setOpenLoading(false);
        setShowToast(true);
        navigate(`/clients/${clientId}/branches`);
      }
    },
    [
      edit,
      clientId,
      setTextLoading,
      setOpenLoading,
      clientBranchService,
      setToastMessage,
      setShowToast,
      navigate,
    ]
  );

  const loadFields = useCallback(async () => {
    if (edit) {
      const searchParams = new URLSearchParams(location.search);
      const id = Number(searchParams.get("id"));
      let branch = location.state;

      if (!isNaN(id) && branch?.id !== id) {
        try {
          const res = await clientBranchService.get(id);
          branch = { ...res.data.result };
        } catch {
          setToastMessage([
            {
              severity: "error",
              primary: `Error al intentar obtener los datos de la sucursal`,
            },
          ]);
          setShowToast(true);
          navigate(`/clients/${clientId}/branches`);
        }
      }
      setValue("id", branch.id);
      setValue("branchName", branch.branchName);
      setValue("personInChargeName", branch.personInChargeName);
      setValue("personInChargePhone", branch.personInChargePhone);
      setValue("address", branch.address);
    } else {
      reset();
    }
  }, [
    location,
    navigate,
    clientId,
    clientBranchService,
    setToastMessage,
    setShowToast,
    edit,
    reset,
    setValue,
  ]);

  useEffect(() => {
    setTitle(edit ? "Editar Sucursal" : "Agregar Sucursal");
    setSearchOptions({
      action: null,
    });
    setBtnPrimary({
      label: edit ? "Actualizar sucursal" : "Enviar datos",
      action: () => handleSubmit(onSubmit)(),
      icon: edit ? "rotate" : "paper-plane",
    });
  }, [edit, setTitle, setSearchOptions, setBtnPrimary, handleSubmit, onSubmit]);

  useEffect(() => {
    if (!clientId || isNaN(clientId)) {
      navigate("/clients");
      return;
    }
    loadFields();
  }, [clientId, loadFields, navigate]);

  const classes = {
    root: {
      maxWidth: "md",
      ml: bpMdDown ? 0 : 8,
      mt: 8,
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    inputControl: {
      width: bpMdDown ? "100%" : "47%",
    },
  };

  return (
    <Box component="form" autoComplete="off" noValidate sx={classes.root}>
      <input type="hidden" {...register("id")} defaultValue={0} />
      <FormControl sx={classes.inputControl}>
        <TextField
          label="Nombre de la sucursal"
          variant="filled"
          sx={{ marginBottom: 4 }}
          InputLabelProps={edit ? { shrink: true } : null}
          {...register("branchName", { required: true })}
          error={errors["branchName"] !== undefined}
          helperText={validateField(errors["branchName"])}
        />
      </FormControl>
      <FormControl sx={classes.inputControl}>
        <TextField
          label="Dirección"
          variant="filled"
          sx={{ marginBottom: 4 }}
          InputLabelProps={edit ? { shrink: true } : null}
          {...register("address", {
            pattern: /^[#.0-9a-zA-Z\s,-]+$/,
          })}
          error={errors["address"] !== undefined}
          helperText={validateField(errors["address"])}
        />
      </FormControl>
      <FormControl sx={classes.inputControl}>
        <TextField
          label="Persona encargada"
          variant="filled"
          sx={{ marginBottom: 4 }}
          InputLabelProps={edit ? { shrink: true } : null}
          {...register("personInChargeName", {
            required: true,
            pattern: /^[a-zA-Z0-9ñÑ][a-zA-Z0-9 ñÑ]*$/,
          })}
          error={errors["personInChargeName"] !== undefined}
          helperText={validateField(errors["personInChargeName"])}
        />
      </FormControl>
      <FormControl sx={classes.inputControl}>
        <TextField
          label="Teléfono"
          type="number"
          variant="filled"
          sx={{ marginBottom: 4 }}
          InputLabelProps={edit ? { shrink: true } : null}
          {...register("personInChargePhone", {
            required: true,
            minLength: 6,
            maxLength: 20,
          })}
          error={errors["personInChargePhone"] !== undefined}
          helperText={validateField(errors["personInChargePhone"], 6, 20)}
        />
      </FormControl>
    </Box>
  );
};

export default NewClientBranch;

